import Axios from "axios";

import { authFetcher } from "../hooks/useAuthSWR";

const startUpload = async (body: { filename: string }) => {
  const { filename } = body;
  const { data: resData } = await authFetcher("/upload/start", {
    method: "post",
    data: {
      file: filename,
    },
  });
  return resData;
};

const getUploadPresignedUrl = async (body: { uploadId: string; videoId?: string; partNumber: number }) => {
  const { uploadId, videoId, partNumber } = body;
  const { data: resData } = await authFetcher(`/upload/${encodeURIComponent(uploadId)}/multi-part`, {
    method: "post",
    data: {
      video_id: videoId,
      part_number: partNumber,
    },
  });
  return resData.pre_signed_url;
};

const uploadPart = async (url: string, chunk: Blob) => {
  const axios = Axios.create();
  axios.defaults.headers.put["Content-Type"] = "application/octet-stream";
  // eslint-disable-next-line compat/compat
  const res = await axios.put(url, chunk);

  return res;
};

const completeUploadAPI = async (body: {
  uploadId: string;
  videoId?: string;
  parts: Array<{ ETag: string; PartNumber: number }>;
}) => {
  const { uploadId, videoId, parts } = body;
  await authFetcher(`/upload/${encodeURIComponent(uploadId)}/multi-part/complete`, {
    method: "post",
    data: {
      video_id: videoId,
      complete_parts: parts.map((part) => ({
        part_number: part.PartNumber,
        e_tag: part.ETag,
      })),
    },
  });
};

interface Part {
  PartNumber: number;
  ETag: string;
}

interface UploaderParams {
  onProgress: (progress: number) => void;
  onError: (error: Error) => void;
  onComplete: () => void;
  file: File;
}

// Refactor: can be set by parameter
const chunkSize = 1024 * 1024 * 5; // minimun size for one part upload is 5MB(https://docs.amazonaws.cn/AWSJavaScriptSDK/latest/AWS/S3.html#uploadPart-property)

class MultipartUploader {
  uploadId?: string;

  videoId?: string;

  file: File;

  parts: Part[];

  isCancel: boolean;

  onProgress: (progress: number) => void;

  onError: (error: Error) => void;

  onComplete: () => void;

  constructor(params: UploaderParams) {
    this.file = params.file;
    this.parts = [];
    this.isCancel = false;
    this.onProgress = params.onProgress;
    this.onError = params.onError;
    this.onComplete = params.onComplete;
  }

  async uploadChunk(partNumber: number) {
    try {
      if (this.uploadId && this.videoId) {
        const chunkNum = this.file ? Math.ceil(this.file.size / chunkSize) : 0;
        const chunk = this.file.slice((partNumber - 1) * chunkSize, partNumber * chunkSize);
        const presignedUrl = await getUploadPresignedUrl({
          uploadId: this.uploadId,
          videoId: this.videoId,
          partNumber,
        });
        const data = await uploadPart(presignedUrl, chunk);
        if (!data) {
          // abort upload api
        } else {
          this.parts = [...this.parts, { ETag: data.headers.etag, PartNumber: partNumber }];
          this.onProgress(chunkNum && Math.floor((this.parts.length / chunkNum) * 10000) / 100);
        }
      } else {
        throw new Error(`upload chunk failed: ${partNumber}`);
      }
    } catch (error) {
      // abort upload api
      console.error("> MultipartUploader > uploadChunk ", error);
      this.isCancel = true;
      this.onError(error);
    }
  }

  async uploadParts(index: number) {
    try {
      if (!this.uploadId) {
        throw new Error("No upload id");
      }
      if (!this.videoId) {
        throw new Error("No video id");
      }
      if (this.isCancel) {
        throw new Error("canceled!");
      }
      const chunkNum = this.file ? Math.ceil(this.file.size / chunkSize) : 0;
      if (index < chunkNum) {
        await this.uploadChunk(index + 1);
        this.uploadParts(index + 1);
      } else if (index === chunkNum) {
        const sortedParts = this.parts.sort((a, b) => a.PartNumber - b.PartNumber); // parts must be in PartNumber ascending order
        await completeUploadAPI({
          uploadId: this.uploadId,
          videoId: this.videoId,
          parts: sortedParts,
        });
        this.onComplete();
      } else {
        throw new Error("Part number does not match");
      }
    } catch (error) {
      // abort upload api
      console.error("> MultipartUploader > uploadParts ", error);
      this.isCancel = true;
      this.onError(error);
    }
  }

  async upload() {
    try {
      if (this.file.name) {
        const startUploadResponse = await startUpload({
          filename: this.file.name,
        });
        this.uploadId = startUploadResponse.upload_id;
        this.videoId = startUploadResponse.video_id;
        this.uploadParts(0);
      }
    } catch (error) {
      // abort upload api
      console.error("> MultipartUploader > upload ", error);
      this.isCancel = true;
      this.onError(error);
    }
  }

  abort() {
    this.isCancel = true;
  }
}

export default MultipartUploader;
